import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Container, Typography, Avatar, Chip, CircularProgress,
    Stack, Paper, Button, IconButton, TextField, MenuItem,
    Dialog, DialogTitle, DialogContent, DialogActions, Alert,
    InputAdornment, Collapse, Grid, Tooltip,
} from '@mui/material';
import ArrowBackIcon      from '@mui/icons-material/ArrowBack';
import AccessTimeIcon     from '@mui/icons-material/AccessTime';
import SortIcon           from '@mui/icons-material/Sort';
import FilterListIcon     from '@mui/icons-material/FilterList';
import CloseIcon          from '@mui/icons-material/Close';
import SearchIcon         from '@mui/icons-material/Search';
import LocationOnIcon     from '@mui/icons-material/LocationOn';
import EmailIcon          from '@mui/icons-material/Email';
import LocationCityIcon   from '@mui/icons-material/LocationCity';
import MapIcon            from '@mui/icons-material/Map';
import GroupsIcon         from '@mui/icons-material/Groups';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import EditIcon           from '@mui/icons-material/Edit';
import AddIcon            from '@mui/icons-material/Add';
import DirectionsBikeIcon      from '@mui/icons-material/DirectionsBike';
import QrCodeIcon              from '@mui/icons-material/QrCode';
import DeleteIcon              from '@mui/icons-material/Delete';
import LockIcon                from '@mui/icons-material/Lock';
import EventSeatIcon           from '@mui/icons-material/EventSeat';
import ShoppingBagIcon         from '@mui/icons-material/ShoppingBag';
import SportsMotorsportsIcon   from '@mui/icons-material/SportsMotorsports';
import Visibility         from '@mui/icons-material/Visibility';
import VisibilityOff      from '@mui/icons-material/VisibilityOff';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation }         from 'react-i18next';
import axios                      from '../api/axiosConfig';
import { useAuth }                from '../context/AuthContext';

const TERRACOTTA = '#C4622D';
const WALNUT     = '#2C1F14';
const FOREST     = '#4A6741';
const GOLD       = '#C9973A';
const PARCHMENT  = '#F7F3EE';

const BIKE_TYPES = ['ELECTRIC', 'ROAD', 'MOUNTAIN', 'NORMAL'];
const CONDITIONS = ['NEW', 'GOOD', 'NEEDS_REPAIR', 'BROKEN'];

const typeColor = (type) => {
    const map = {
        CITY:      { bgcolor: FOREST,     color: 'white' },
        METROPOLIS:{ bgcolor: WALNUT,     color: 'white' },
        MEGAPOLIS: { bgcolor: '#5C1A1A',  color: 'white' },
        TOWN:      { bgcolor: GOLD,       color: WALNUT  },
        VILLAGE:   { bgcolor: TERRACOTTA, color: 'white' },
    };
    return map[type] || { bgcolor: '#999', color: 'white' };
};

const conditionColor = (c) => {
    const map = {
        NEW:          { bgcolor: '#e8f5e9', color: '#388e3c' },
        GOOD:         { bgcolor: '#e3f2fd', color: '#1976d2' },
        NEEDS_REPAIR: { bgcolor: '#fff3e0', color: '#f57c00' },
        BROKEN:       { bgcolor: '#ffebee', color: '#d32f2f' },
    };
    return map[c] || { bgcolor: '#f5f5f5', color: '#666' };
};

// ── Info row ──────────────────────────────────────────────────────────────────
function InfoRow({ icon, label, value }) {
    return (
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 1.5,
            borderBottom: '1px solid rgba(196,98,45,0.08)', '&:last-child': { border: 'none' } }}>
            <Box sx={{ color: TERRACOTTA, mt: 0.2, flexShrink: 0 }}>{icon}</Box>
            <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600,
                    textTransform: 'uppercase', letterSpacing: '0.07em', fontSize: '0.68rem' }}>
                    {label}
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: value ? WALNUT : 'text.disabled' }}>
                    {value || '—'}
                </Typography>
            </Box>
        </Stack>
    );
}

// ── Staff card ────────────────────────────────────────────────────────────────
function StaffCard({ name, email, imageUrl, sub }) {
    return (
        <Paper elevation={0} sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5,
            border: '1px solid rgba(196,98,45,0.1)', borderRadius: 2, bgcolor: 'white',
            '&:hover': { boxShadow: '0 2px 10px rgba(44,31,20,0.07)', borderColor: 'rgba(196,98,45,0.2)' },
        }}>
            <Avatar src={imageUrl} alt={name}
                sx={{ width: 40, height: 40, bgcolor: TERRACOTTA, border: '2px solid rgba(196,98,45,0.15)', flexShrink: 0 }}>
                {name?.[0]?.toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={700} sx={{ color: WALNUT, lineHeight: 1.2 }}>{name}</Typography>
                {sub && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{sub}</Typography>}
                {email && <Typography variant="caption" display="block" sx={{ color: 'text.disabled', fontSize: '0.68rem' }}>{email}</Typography>}
            </Box>
        </Paper>
    );
}

// ── Section header ────────────────────────────────────────────────────────────
function SectionHeader({ icon, title, count }) {
    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ color: TERRACOTTA }}>{icon}</Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: WALNUT }}>{title}</Typography>
            <Chip label={count} size="small"
                sx={{ bgcolor: 'rgba(196,98,45,0.1)', color: TERRACOTTA, fontWeight: 700, height: 20, fontSize: '0.72rem' }} />
        </Stack>
    );
}

// ── Delete district dialog ────────────────────────────────────────────────────
function DeleteDistrictDialog({ open, districtName, districtId, currentUser, onClose, onDeleted }) {
    const { t } = useTranslation();
    const [password,   setPassword]   = useState('');
    const [showPwd,    setShowPwd]    = useState(false);
    const [pwdError,   setPwdError]   = useState('');
    const [loading,    setLoading]    = useState(false);

    useEffect(() => { if (open) { setPassword(''); setPwdError(''); setShowPwd(false); } }, [open]);

    const handleDelete = async () => {
        if (!password) { setPwdError(t('users.password_required')); return; }
        setLoading(true); setPwdError('');
        try {
            await axios.post('/api/app_user/login', { email: currentUser.email, password });
            await axios.delete(`/districts/delete/${districtId}`);
            onDeleted();
        } catch (err) {
            const code = err.response?.data?.error; const st = err.response?.status;
            setPwdError(st === 401 || code === 'WRONG_PASSWORD' || code === 'INVALID_CREDENTIALS'
                ? t('users.wrong_password')
                : t('districts.delete_failed', 'Failed to delete district.'));
        } finally { setLoading(false); }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle fontWeight={700} sx={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#d32f2f' }}>
                {t('districts.delete_title', 'Delete District')}
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('districts.delete_text', 'This will permanently delete this district. Enter your password to confirm.')}
                    {districtName && (
                        <Box component="span" sx={{ display: 'block', mt: 0.5, fontWeight: 700, color: WALNUT }}>
                            {districtName}
                        </Box>
                    )}
                </Typography>
                <TextField fullWidth label={t('users.your_password')} type={showPwd ? 'text' : 'password'}
                    value={password} onChange={e => { setPassword(e.target.value); setPwdError(''); }}
                    onKeyDown={e => { if (e.key === 'Enter') handleDelete(); }} autoFocus
                    InputProps={{ endAdornment: (
                        <InputAdornment position="end">
                            <IconButton onClick={() => setShowPwd(p => !p)} edge="end">
                                {showPwd ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </InputAdornment>
                    )}}
                />
                {pwdError && <Alert severity="error" sx={{ mt: 1.5 }}>{pwdError}</Alert>}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button onClick={onClose} disabled={loading}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={handleDelete} disabled={loading}
                    sx={{ bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' }, borderRadius: 2, fontWeight: 700 }}>
                    {loading ? t('bicycle.deleting', 'Deleting…') : t('districts.delete_btn', 'Delete')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ── Edit district dialog ──────────────────────────────────────────────────────
function EditDistrictDialog({ open, district, onClose, onSaved }) {
    const { t } = useTranslation();
    const [form, setForm]     = useState({});
    const [saving, setSaving] = useState(false);
    const [error, setError]   = useState('');

    useEffect(() => {
        if (district) setForm({
            name: district.name || '', region: district.region || '',
            address: district.address || '', email: district.email || '', type: district.type || '',
            latitude: district.latitude ?? '', longitude: district.longitude ?? '',
        });
    }, [district]);

    const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

    const handleSave = async () => {
        setSaving(true); setError('');
        try {
            const res = await axios.put(`/districts/edit/${district.id}`, form);
            onSaved(res.data); onClose();
        } catch { setError(t('districts.save_failed', 'Failed to save changes.')); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle fontWeight={700} sx={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                {t('districts.edit_title', 'Edit District')}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField label={t('districts.name', 'Name')} value={form.name || ''} onChange={e => set('name', e.target.value)} fullWidth autoFocus />
                    <TextField label={t('districts.region', 'Region')} value={form.region || ''} onChange={e => set('region', e.target.value)} fullWidth />
                    <TextField label={t('districts.address', 'Address')} value={form.address || ''} onChange={e => set('address', e.target.value)} fullWidth />
                    <TextField label={t('districts.email', 'Email')} value={form.email || ''} onChange={e => set('email', e.target.value)} fullWidth />
                    <TextField label={t('districts.type', 'Type')} value={form.type || ''} onChange={e => set('type', e.target.value)} fullWidth select SelectProps={{ native: true }}>
                        {['CITY', 'VILLAGE', 'TOWN', 'METROPOLIS', 'MEGAPOLIS'].map(tp => <option key={tp} value={tp}>{tp}</option>)}
                    </TextField>
                    <Stack direction="row" spacing={2}>
                        <TextField label={t('districts.latitude', 'Latitude')} value={form.latitude} type="number"
                            onChange={e => set('latitude', e.target.value === '' ? '' : Number(e.target.value))} fullWidth
                            inputProps={{ step: 'any' }} />
                        <TextField label={t('districts.longitude', 'Longitude')} value={form.longitude} type="number"
                            onChange={e => set('longitude', e.target.value === '' ? '' : Number(e.target.value))} fullWidth
                            inputProps={{ step: 'any' }} />
                    </Stack>
                    {error && <Alert severity="error">{error}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button onClick={onClose} disabled={saving}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}
                    sx={{ bgcolor: TERRACOTTA, '&:hover': { bgcolor: '#9A4A1F' }, borderRadius: 2, fontWeight: 700 }}>
                    {saving ? t('common.saving') : t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ── Bicycle row ───────────────────────────────────────────────────────────────
function BicycleRow({ bicycle, onEdit, onAssignCode, onDelete }) {
    const { t } = useTranslation();
    const cc = conditionColor(bicycle.condition);
    return (
        <Paper elevation={0} sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5,
            border: '1px solid rgba(196,98,45,0.1)', borderRadius: 2, bgcolor: 'white', transition: '0.15s',
            '&:hover': { boxShadow: '0 2px 10px rgba(44,31,20,0.07)', borderColor: 'rgba(196,98,45,0.2)' },
        }}>
            {/* Availability dot */}
            <Box sx={{
                width: 12, height: 12, borderRadius: '50%', flexShrink: 0,
                bgcolor: bicycle.available ? '#4caf50' : '#f44336',
                boxShadow: bicycle.available ? '0 0 0 3px rgba(76,175,80,0.2)' : '0 0 0 3px rgba(244,67,54,0.2)',
            }} />

            <DirectionsBikeIcon sx={{ color: TERRACOTTA, fontSize: '1.1rem', flexShrink: 0 }} />

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="body2" fontWeight={700} sx={{ color: WALNUT }}>{bicycle.type}</Typography>
                    {bicycle.code ? (
                        <Chip label={bicycle.code} size="small"
                            sx={{ bgcolor: WALNUT, color: 'white', fontWeight: 800, fontSize: '0.68rem', fontFamily: 'monospace', height: 18 }} />
                    ) : (
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                            {t('bicycle.no_code', 'No code')}
                        </Typography>
                    )}
                </Stack>
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                    {bicycle.available ? t('bicycle.available', 'Available') : t('bicycle.in_use', 'In use')}
                    {bicycle.addedByUsername && (
                        <> · {t('bicycle.added_by', 'Added by')} <Box component="span" fontWeight={600}>{bicycle.addedByUsername}</Box></>
                    )}
                </Typography>
            </Box>

            <Chip label={bicycle.condition?.replace('_', ' ')} size="small" sx={{ ...cc, fontWeight: 700, fontSize: '0.7rem' }} />

            <IconButton size="small" onClick={() => onAssignCode(bicycle)} title={t('bicycle.assign_code_btn', 'Assign code')}
                sx={{ color: FOREST, border: '1px solid rgba(74,103,65,0.3)', borderRadius: 1.5 }}>
                <QrCodeIcon fontSize="small" />
            </IconButton>

            <IconButton size="small" onClick={() => onEdit(bicycle)} title={t('common.edit')}
                sx={{ color: TERRACOTTA, border: '1px solid rgba(196,98,45,0.2)', borderRadius: 1.5 }}>
                <EditIcon fontSize="small" />
            </IconButton>

            <IconButton size="small" onClick={() => onDelete(bicycle)} title={t('common.delete')}
                sx={{ color: '#d32f2f', border: '1px solid rgba(211,47,47,0.2)', borderRadius: 1.5 }}>
                <DeleteIcon fontSize="small" />
            </IconButton>
        </Paper>
    );
}

// ── Assign code dialog ────────────────────────────────────────────────────────
function AssignCodeDialog({ open, bicycle, districtName, onClose, onSaved }) {
    const { t } = useTranslation();
    const prefix = districtName
        ? districtName.trim().split(/\s+/).filter(Boolean).map(w => w[0].toUpperCase()).join('')
        : '';

    const [num,    setNum]    = useState('');
    const [saving, setSaving] = useState(false);
    const [error,  setError]  = useState('');

    useEffect(() => { if (open) { setNum(''); setError(''); } }, [open, bicycle]);

    const preview = num.length > 0 ? `${prefix}${String(Number(num)).padStart(3, '0')}` : `${prefix}___`;

    const handleSave = async () => {
        const n = Number(num);
        if (!num || isNaN(n) || n < 1 || n > 999) { setError(t('bicycle.code_range_error', 'Enter a number between 1 and 999.')); return; }
        setSaving(true); setError('');
        try {
            const res = await axios.patch(`/bicycles/${bicycle.id}/code`, { codeNumber: n });
            onSaved(res.data); onClose();
        } catch (err) {
            setError(err.response?.status === 409
                ? t('bicycle.code_conflict', `Code "${preview}" is already in use. Try a different number.`)
                : t('bicycle.code_save_failed', 'Failed to assign code.'));
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle fontWeight={700} sx={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                {t('bicycle.assign_code_title', 'Assign Bicycle Code')}
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('bicycle.assign_code_hint', 'Enter a 3-digit number (1–999). The district prefix is added automatically.')}
                </Typography>
                <Box sx={{ textAlign: 'center', mb: 2.5 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600 }}>
                        {t('bicycle.code_preview', 'Preview')}
                    </Typography>
                    <Typography variant="h4" fontWeight={900} fontFamily="monospace"
                        sx={{ color: num ? WALNUT : 'text.disabled', letterSpacing: '0.05em' }}>
                        {preview}
                    </Typography>
                </Box>
                <TextField
                    label={t('bicycle.code_number', 'Number (1–999)')} type="number" value={num}
                    onChange={e => { setNum(e.target.value); setError(''); }}
                    onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
                    fullWidth autoFocus inputProps={{ min: 1, max: 999 }}
                    InputProps={{ startAdornment: (
                        <InputAdornment position="start">
                            <Typography fontWeight={700} fontFamily="monospace" sx={{ color: TERRACOTTA }}>{prefix}</Typography>
                        </InputAdornment>
                    )}}
                />
                {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button onClick={onClose} disabled={saving}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}
                    sx={{ bgcolor: FOREST, '&:hover': { bgcolor: '#3a5233' }, borderRadius: 2, fontWeight: 700 }}>
                    {saving ? t('common.saving') : t('bicycle.assign_code_btn', 'Assign Code')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ── Edit bicycle dialog ───────────────────────────────────────────────────────
function EditBicycleDialog({ open, bicycle, districts, isAdmin, onClose, onSaved, currentUser }) {
    const { t } = useTranslation();
    const [form, setForm]         = useState({ condition: '', districtId: '' });
    const [formError, setFormError] = useState('');
    const [pwdOpen,    setPwdOpen]    = useState(false);
    const [password,   setPassword]   = useState('');
    const [showPwd,    setShowPwd]    = useState(false);
    const [pwdError,   setPwdError]   = useState('');
    const [pwdLoading, setPwdLoading] = useState(false);

    useEffect(() => {
        if (bicycle) { setForm({ condition: bicycle.condition || '', districtId: bicycle.districtId || '' }); setFormError(''); }
    }, [bicycle]);

    const submitEdit = () => {
        const conditionChanged = form.condition  && form.condition  !== bicycle?.condition;
        const districtChanged  = isAdmin && form.districtId && form.districtId !== bicycle?.districtId;
        if (!conditionChanged && !districtChanged) { setFormError(t('bicycle.nothing_changed', 'No changes to save.')); return; }
        setFormError(''); onClose(); setPwdOpen(true);
    };

    const closePwd = () => { setPwdOpen(false); setPassword(''); setPwdError(''); setShowPwd(false); };

    const confirmSave = async () => {
        if (!password) { setPwdError(t('users.password_required')); return; }
        setPwdLoading(true); setPwdError('');
        try {
            await axios.post('/api/app_user/login', { email: currentUser.email, password });
            const payload = {};
            if (form.condition)             payload.condition  = form.condition;
            if (isAdmin && form.districtId) payload.districtId = form.districtId;
            const res = await axios.patch(`/bicycles/edit/${bicycle.id}`, payload);
            onSaved(res.data); closePwd();
        } catch (err) {
            const code = err.response?.data?.error; const st = err.response?.status;
            setPwdError(st === 401 || code === 'WRONG_PASSWORD' || code === 'INVALID_CREDENTIALS'
                ? t('users.wrong_password') : t('bicycle.save_failed', 'Failed to save changes.'));
        } finally { setPwdLoading(false); }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle fontWeight={700} sx={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    {t('bicycle.edit_title', 'Edit Bicycle')}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField label={t('bicycle.condition', 'Condition')} value={form.condition}
                            onChange={e => setForm(f => ({ ...f, condition: e.target.value }))} select fullWidth>
                            {CONDITIONS.map(c => <MenuItem key={c} value={c}>{c.replace('_', ' ')}</MenuItem>)}
                        </TextField>
                        {isAdmin && (
                            <TextField label={t('bicycle.district', 'District')} value={form.districtId}
                                onChange={e => setForm(f => ({ ...f, districtId: e.target.value }))} select fullWidth>
                                {districts.map(d => <MenuItem key={d.id} value={d.id}>{d.name || d.region || d.id}</MenuItem>)}
                            </TextField>
                        )}
                        {formError && <Alert severity="warning">{formError}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={onClose}>{t('common.cancel')}</Button>
                    <Button variant="contained" onClick={submitEdit}
                        sx={{ bgcolor: TERRACOTTA, '&:hover': { bgcolor: '#9A4A1F' }, borderRadius: 2, fontWeight: 700 }}>
                        {t('common.next', 'Next')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={pwdOpen} onClose={closePwd} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle fontWeight={700} sx={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    {t('users.confirm_title')}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t('bicycle.confirm_text', 'Enter your password to confirm the changes.')}
                    </Typography>
                    <TextField fullWidth label={t('users.your_password')} type={showPwd ? 'text' : 'password'}
                        value={password} onChange={e => { setPassword(e.target.value); setPwdError(''); }}
                        onKeyDown={e => { if (e.key === 'Enter') confirmSave(); }} autoFocus
                        InputProps={{ endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowPwd(p => !p)} edge="end">
                                    {showPwd ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        )}}
                    />
                    {pwdError && <Alert severity="error" sx={{ mt: 1.5 }}>{pwdError}</Alert>}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={closePwd} disabled={pwdLoading}>{t('common.cancel')}</Button>
                    <Button variant="contained" onClick={confirmSave} disabled={pwdLoading}
                        sx={{ bgcolor: TERRACOTTA, '&:hover': { bgcolor: '#9A4A1F' }, borderRadius: 2, fontWeight: 700 }}>
                        {pwdLoading ? t('common.saving') : t('users.confirm_btn')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

// ── Delete bicycle dialog ─────────────────────────────────────────────────────
function DeleteBicycleDialog({ open, bicycle, onClose, onDeleted, currentUser }) {
    const { t } = useTranslation();
    const [password,   setPassword]   = useState('');
    const [showPwd,    setShowPwd]    = useState(false);
    const [pwdError,   setPwdError]   = useState('');
    const [loading,    setLoading]    = useState(false);

    useEffect(() => { if (open) { setPassword(''); setPwdError(''); setShowPwd(false); } }, [open]);

    const handleDelete = async () => {
        if (!password) { setPwdError(t('users.password_required')); return; }
        setLoading(true); setPwdError('');
        try {
            await axios.post('/api/app_user/login', { email: currentUser.email, password });
            await axios.delete(`/bicycles/${bicycle.id}`);
            onDeleted(bicycle.id); onClose();
        } catch (err) {
            const code = err.response?.data?.error; const st = err.response?.status;
            setPwdError(st === 401 || code === 'WRONG_PASSWORD' || code === 'INVALID_CREDENTIALS'
                ? t('users.wrong_password') : t('bicycle.delete_failed', 'Failed to delete bicycle.'));
        } finally { setLoading(false); }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle fontWeight={700} sx={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#d32f2f' }}>
                {t('bicycle.delete_title', 'Delete Bicycle')}
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('bicycle.delete_text', 'This will permanently delete this bicycle. Enter your password to confirm.')}
                    {bicycle?.code && (
                        <Box component="span" sx={{ display: 'block', mt: 0.5, fontWeight: 700, color: WALNUT }}>
                            {bicycle.code}
                        </Box>
                    )}
                </Typography>
                <TextField fullWidth label={t('users.your_password')} type={showPwd ? 'text' : 'password'}
                    value={password} onChange={e => { setPassword(e.target.value); setPwdError(''); }}
                    onKeyDown={e => { if (e.key === 'Enter') handleDelete(); }} autoFocus
                    InputProps={{ endAdornment: (
                        <InputAdornment position="end">
                            <IconButton onClick={() => setShowPwd(p => !p)} edge="end">
                                {showPwd ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </InputAdornment>
                    )}}
                />
                {pwdError && <Alert severity="error" sx={{ mt: 1.5 }}>{pwdError}</Alert>}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button onClick={onClose} disabled={loading}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={handleDelete} disabled={loading}
                    sx={{ bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' }, borderRadius: 2, fontWeight: 700 }}>
                    {loading ? t('bicycle.deleting', 'Deleting…') : t('bicycle.delete_btn', 'Delete')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ── Edit equipment dialog ─────────────────────────────────────────────────────
function EditEquipmentDialog({ open, item, onClose, onSaved }) {
    const { t } = useTranslation();
    const [form,   setForm]   = useState({ condition: '', status: '' });
    const [saving, setSaving] = useState(false);
    const [error,  setError]  = useState('');

    useEffect(() => {
        if (item) { setForm({ condition: item.condition || '', status: item.status || '' }); setError(''); }
    }, [item]);

    const handleSave = async () => {
        setSaving(true); setError('');
        try {
            const res = await axios.patch(`/equipment/edit/${item.id}`, form);
            onSaved(res.data); onClose();
        } catch { setError(t('equipment.save_failed', 'Failed to save changes.')); }
        finally  { setSaving(false); }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle fontWeight={700} sx={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                {t('equipment.edit_title', 'Edit Equipment')}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField select fullWidth label={t('bicycle.condition', 'Condition')}
                        value={form.condition} onChange={e => setForm(f => ({ ...f, condition: e.target.value }))}>
                        {CONDITIONS.map(c => <MenuItem key={c} value={c}>{c.replace(/_/g, ' ')}</MenuItem>)}
                    </TextField>
                    <TextField select fullWidth label={t('equipment.status', 'Status')}
                        value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                        {['AVAILABLE', 'UNDER_MAINTENANCE', 'DECOMMISSIONED'].map(s => (
                            <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>
                        ))}
                    </TextField>
                    {error && <Alert severity="error">{error}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button onClick={onClose} disabled={saving}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}
                    sx={{ bgcolor: TERRACOTTA, '&:hover': { bgcolor: '#9A4A1F' }, borderRadius: 2, fontWeight: 700 }}>
                    {saving ? t('common.saving') : t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ── Delete equipment dialog ───────────────────────────────────────────────────
function DeleteEquipmentDialog({ open, item, onClose, onDeleted, currentUser }) {
    const { t } = useTranslation();
    const [password, setPassword] = useState('');
    const [showPwd,  setShowPwd]  = useState(false);
    const [pwdError, setPwdError] = useState('');
    const [loading,  setLoading]  = useState(false);

    useEffect(() => { if (open) { setPassword(''); setPwdError(''); setShowPwd(false); } }, [open]);

    const handleDelete = async () => {
        if (!password) { setPwdError(t('users.password_required')); return; }
        setLoading(true); setPwdError('');
        try {
            await axios.post('/api/app_user/login', { email: currentUser.email, password });
            await axios.delete(`/equipment/${item.id}`);
            onDeleted(item.id); onClose();
        } catch (err) {
            const code = err.response?.data?.error; const st = err.response?.status;
            setPwdError(st === 401 || code === 'WRONG_PASSWORD' || code === 'INVALID_CREDENTIALS'
                ? t('users.wrong_password') : t('equipment.delete_failed', 'Failed to delete equipment.'));
        } finally { setLoading(false); }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle fontWeight={700} sx={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#d32f2f' }}>
                {t('equipment.delete_title', 'Delete Equipment')}
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('equipment.delete_text', 'This will permanently delete this item. Enter your password to confirm.')}
                    {item?.code && (
                        <Box component="span" sx={{ display: 'block', mt: 0.5, fontWeight: 700, color: WALNUT }}>{item.code}</Box>
                    )}
                </Typography>
                <TextField fullWidth label={t('users.your_password')} type={showPwd ? 'text' : 'password'}
                    value={password} onChange={e => { setPassword(e.target.value); setPwdError(''); }}
                    onKeyDown={e => { if (e.key === 'Enter') handleDelete(); }} autoFocus
                    InputProps={{ endAdornment: (
                        <InputAdornment position="end">
                            <IconButton onClick={() => setShowPwd(p => !p)} edge="end">
                                {showPwd ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </InputAdornment>
                    )}}
                />
                {pwdError && <Alert severity="error" sx={{ mt: 1.5 }}>{pwdError}</Alert>}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button onClick={onClose} disabled={loading}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={handleDelete} disabled={loading}
                    sx={{ bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' }, borderRadius: 2, fontWeight: 700 }}>
                    {loading ? t('bicycle.deleting', 'Deleting…') : t('bicycle.delete_btn', 'Delete')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ── Add equipment dialog ──────────────────────────────────────────────────────
function AddEquipmentDialog({ open, equipmentType, districtId, onClose, onAdded }) {
    const { t } = useTranslation();
    const [form,     setForm]     = useState({ condition: 'GOOD', code: '' });
    const [quantity, setQuantity] = useState(1);
    const [saving,   setSaving]   = useState(false);
    const [error,    setError]    = useState('');

    useEffect(() => {
        if (open) { setForm({ condition: 'GOOD', code: '' }); setQuantity(1); setError(''); }
    }, [open]);

    const handleSave = async () => {
        const qty = Math.max(1, Math.min(20, Number(quantity) || 1));
        setSaving(true); setError('');
        try {
            for (let i = 0; i < qty; i++) {
                const res = await axios.post('/equipment', {
                    equipmentType,
                    districtId,
                    condition: form.condition,
                    code: qty === 1 ? (form.code.trim() || null) : null,
                });
                onAdded(res.data);
            }
            onClose();
        } catch (err) {
            setError(err.response?.status === 409
                ? t('equipment.code_conflict', 'That code is already in use.')
                : t('equipment.add_failed', 'Failed to add equipment.'));
        } finally { setSaving(false); }
    };

    const typeLabel = { HELMET: 'Helmet', LOCK: 'Lock', KID_SEAT: 'Kid Seat', BAG: 'Bag' }[equipmentType] || equipmentType;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle fontWeight={700} sx={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                {t('equipment.add_title', 'Add {{type}}', { type: typeLabel })}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        fullWidth type="number" label={t('equipment.quantity', 'Quantity')}
                        value={quantity} onChange={e => setQuantity(e.target.value)}
                        inputProps={{ min: 1, max: 20 }}
                        helperText={t('equipment.quantity_hint', 'Add 1–20 items at once')} />
                    <TextField select fullWidth label={t('bicycle.condition', 'Condition')}
                        value={form.condition} onChange={e => setForm(f => ({ ...f, condition: e.target.value }))}>
                        {CONDITIONS.map(c => <MenuItem key={c} value={c}>{c.replace(/_/g, ' ')}</MenuItem>)}
                    </TextField>
                    {Number(quantity) === 1 && (
                        <TextField fullWidth label={t('equipment.code_optional', 'Code (optional)')}
                            value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
                            inputProps={{ maxLength: 20 }} />
                    )}
                    {error && <Alert severity="error">{error}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button onClick={onClose} disabled={saving}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}
                    sx={{ bgcolor: TERRACOTTA, '&:hover': { bgcolor: '#9A4A1F' }, borderRadius: 2, fontWeight: 700 }}>
                    {saving ? t('common.saving') : t('equipment.add_btn', '+ Add')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ── Equipment status dot helper ───────────────────────────────────────────────
const statusDotStyle = (status) => {
    const map = {
        AVAILABLE:         { bgcolor: '#4caf50', shadow: 'rgba(76,175,80,0.2)'  },
        UNDER_MAINTENANCE: { bgcolor: '#ff9800', shadow: 'rgba(255,152,0,0.2)'  },
        DECOMMISSIONED:    { bgcolor: '#9e9e9e', shadow: 'rgba(158,158,158,0.2)'},
    };
    return map[status] || { bgcolor: '#9e9e9e', shadow: 'rgba(158,158,158,0.2)' };
};

// ── Generic equipment row ─────────────────────────────────────────────────────
function EquipmentRow({ item, icon, onEdit, onDelete, canEdit }) {
    const { t } = useTranslation();
    const cc  = conditionColor(item.condition);
    const dot = statusDotStyle(item.status);
    return (
        <Paper elevation={0} sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5,
            border: '1px solid rgba(196,98,45,0.1)', borderRadius: 2, bgcolor: 'white', transition: '0.15s',
            '&:hover': { boxShadow: '0 2px 10px rgba(44,31,20,0.07)', borderColor: 'rgba(196,98,45,0.2)' },
        }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', flexShrink: 0,
                bgcolor: dot.bgcolor, boxShadow: `0 0 0 3px ${dot.shadow}` }} />
            <Box sx={{ color: TERRACOTTA, fontSize: '1.1rem', display: 'flex', flexShrink: 0 }}>{icon}</Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {item.code ? (
                    <Chip label={item.code} size="small"
                        sx={{ bgcolor: WALNUT, color: 'white', fontWeight: 800, fontSize: '0.68rem', fontFamily: 'monospace', height: 18 }} />
                ) : (
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                        {t('equipment.no_code', 'No code')}
                    </Typography>
                )}
                <Typography variant="caption" display="block" sx={{ color: 'text.disabled' }}>
                    {item.status?.replace(/_/g, ' ')}
                    {item.addedByUsername && (
                        <> · {t('equipment.added_by', 'Added by')} <Box component="span" fontWeight={600}>{item.addedByUsername}</Box></>
                    )}
                </Typography>
            </Box>
            <Chip label={item.condition?.replace(/_/g, ' ')} size="small" sx={{ ...cc, fontWeight: 700, fontSize: '0.7rem' }} />
            {canEdit && (
                <>
                    <IconButton size="small" onClick={() => onEdit(item)} title={t('common.edit')}
                        sx={{ color: TERRACOTTA, border: '1px solid rgba(196,98,45,0.2)', borderRadius: 1.5 }}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => onDelete(item)} title={t('common.delete')}
                        sx={{ color: '#d32f2f', border: '1px solid rgba(211,47,47,0.2)', borderRadius: 1.5 }}>
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </>
            )}
        </Paper>
    );
}

// ── Equipment section block ───────────────────────────────────────────────────
function EquipmentSection({ icon, title, items, loading, emptyKey, emptyDefault, canEdit, onEdit, onDelete, onAdd }) {
    const { t } = useTranslation();
    return (
        <Box sx={{ mt: 4, bgcolor: 'white', borderRadius: 3, overflow: 'hidden',
            boxShadow: '0 2px 16px rgba(44,31,20,0.07)', border: '1px solid rgba(196,98,45,0.1)' }}>
            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid rgba(196,98,45,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <SectionHeader icon={icon} title={title} count={items.length} />
                {canEdit && onAdd && (
                    <Button variant="contained" startIcon={<AddIcon />} size="small" onClick={onAdd}
                        sx={{ bgcolor: TERRACOTTA, '&:hover': { bgcolor: '#9A4A1F' }, borderRadius: 2, fontWeight: 700 }}>
                        {t('equipment.add_btn', '+ Add')}
                    </Button>
                )}
            </Box>
            <Box sx={{ px: 3, py: 2 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={28} sx={{ color: TERRACOTTA }} />
                    </Box>
                ) : items.length > 0 ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' }, gap: 1.5 }}>
                        {items.map(item => (
                            <EquipmentRow key={item.id} item={item} icon={icon}
                                canEdit={canEdit} onEdit={onEdit} onDelete={onDelete} />
                        ))}
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                        {t(emptyKey, emptyDefault)}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

// ── District Hours section ────────────────────────────────────────────────────
const DAYS_OF_WEEK = ['MONDAY','TUESDAY','WEDNESDAY','THURSDAY','FRIDAY','SATURDAY','SUNDAY'];
const DAY_LABELS   = { MONDAY:'Mon', TUESDAY:'Tue', WEDNESDAY:'Wed', THURSDAY:'Thu',
                       FRIDAY:'Fri', SATURDAY:'Sat', SUNDAY:'Sun' };

function DistrictHoursSection({ districtId, canEdit, t }) {
    const [hours,    setHours]    = useState({});   // dayOfWeek → DistrictHoursDto
    const [loading,  setLoading]  = useState(true);
    const [editDay,  setEditDay]  = useState(null);  // currently being edited
    const [editVals, setEditVals] = useState({ openTime: '', closeTime: '' });
    const [saving,   setSaving]   = useState(false);

    useEffect(() => {
        axios.get(`/district-hours/view/district/${districtId}`)
            .then(res => {
                const map = {};
                (res.data || []).forEach(h => { map[h.dayOfWeek] = h; });
                setHours(map);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [districtId]);

    const startEdit = (day) => {
        const h = hours[day];
        setEditVals({ openTime: h?.openTime || '', closeTime: h?.closeTime || '' });
        setEditDay(day);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                districtId,
                dayOfWeek: editDay,
                openTime:  editVals.openTime || null,
                closeTime: editVals.closeTime || null,
            };
            const res = await axios.post('/district-hours/upsert', payload);
            setHours(prev => ({ ...prev, [editDay]: res.data }));
            setEditDay(null);
        } catch {}
        finally { setSaving(false); }
    };

    const handleMarkClosed = async (day) => {
        const existing = hours[day];
        if (!existing) return;
        const res = await axios.put(`/district-hours/edit/${existing.id}`, {
            ...existing, openTime: null, closeTime: null
        });
        setHours(prev => ({ ...prev, [day]: res.data }));
    };

    return (
        <Box sx={{ mt: 4, bgcolor: 'white', borderRadius: 3, overflow: 'hidden',
            boxShadow: '0 2px 16px rgba(44,31,20,0.07)', border: '1px solid rgba(196,98,45,0.1)' }}>
            <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(196,98,45,0.1)',
                display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTimeIcon sx={{ color: TERRACOTTA, fontSize: '1.2rem' }} />
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: WALNUT }}>
                    {t('district.hours_title', 'Opening Hours')}
                </Typography>
            </Box>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} sx={{ color: TERRACOTTA }} />
                </Box>
            ) : (
                <Box sx={{ px: 3, py: 2 }}>
                    <Stack spacing={1}>
                        {DAYS_OF_WEEK.map(day => {
                            const h     = hours[day];
                            const open  = h?.openTime;
                            const close = h?.closeTime;
                            const isEditing = editDay === day;

                            return (
                                <Box key={day} sx={{ display: 'flex', alignItems: 'center', gap: 2,
                                    py: 1.2, borderBottom: '1px solid rgba(196,98,45,0.06)',
                                    '&:last-child': { border: 'none' } }}>
                                    <Typography variant="body2" fontWeight={700} sx={{ color: WALNUT, minWidth: 36 }}>
                                        {DAY_LABELS[day]}
                                    </Typography>
                                    {isEditing ? (
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ flex: 1 }}>
                                            <TextField size="small" type="time" label="Open"
                                                value={editVals.openTime} onChange={e => setEditVals(v => ({ ...v, openTime: e.target.value }))}
                                                sx={{ width: 130 }} InputLabelProps={{ shrink: true }} />
                                            <TextField size="small" type="time" label="Close"
                                                value={editVals.closeTime} onChange={e => setEditVals(v => ({ ...v, closeTime: e.target.value }))}
                                                sx={{ width: 130 }} InputLabelProps={{ shrink: true }} />
                                            <Button size="small" variant="contained" onClick={handleSave} disabled={saving}
                                                sx={{ bgcolor: TERRACOTTA, '&:hover': { bgcolor: '#9A4A1F' }, borderRadius: 1.5, fontWeight: 700 }}>
                                                {saving ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
                                            </Button>
                                            <Button size="small" onClick={() => setEditDay(null)} sx={{ color: 'text.secondary' }}>
                                                {t('common.cancel', 'Cancel')}
                                            </Button>
                                        </Stack>
                                    ) : (
                                        <>
                                            <Box sx={{ flex: 1 }}>
                                                {open ? (
                                                    <Typography variant="body2" sx={{ color: FOREST, fontWeight: 600 }}>
                                                        {open.slice(0,5)} – {close?.slice(0,5) || '?'}
                                                    </Typography>
                                                ) : (
                                                    <Chip label={t('district.closed', 'Closed')} size="small"
                                                        sx={{ bgcolor: 'rgba(211,47,47,0.08)', color: '#d32f2f',
                                                            fontWeight: 700, height: 22, fontSize: '0.7rem' }} />
                                                )}
                                            </Box>
                                            {canEdit && (
                                                <Stack direction="row" spacing={0.5}>
                                                    <IconButton size="small" onClick={() => startEdit(day)}
                                                        sx={{ color: TERRACOTTA, border: '1px solid rgba(196,98,45,0.2)', borderRadius: 1.5 }}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    {open && (
                                                        <Tooltip title={t('district.mark_closed', 'Mark as closed')}>
                                                            <IconButton size="small" onClick={() => handleMarkClosed(day)}
                                                                sx={{ color: '#d32f2f', border: '1px solid rgba(211,47,47,0.2)', borderRadius: 1.5 }}>
                                                                <CloseIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Stack>
                                            )}
                                        </>
                                    )}
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>
            )}
        </Box>
    );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function DistrictDetails() {
    const { id }     = useParams();
    const { user }   = useAuth();
    const navigate   = useNavigate();
    const { t }      = useTranslation();

    const isAdmin   = user?.role === 'ADMIN';
    const isManager = user?.role === 'MANAGER';
    const canEdit   = isAdmin || isManager;

    const [details,    setDetails]    = useState(null);
    const [loading,    setLoading]    = useState(true);
    const [editOpen,    setEditOpen]    = useState(false);
    const [notAllowed,  setNotAllowed]  = useState(false);
    const [delDistOpen, setDelDistOpen] = useState(false);

    // Bicycles
    const [bicycles,     setBicycles]     = useState([]);
    const [bikeLoading,  setBikeLoading]  = useState(true);
    const [districts,    setDistricts]    = useState([]);

    // Other equipment (helmets, locks, kid seats, bags)
    const [equipment,    setEquipment]    = useState([]);
    const [equipLoading, setEquipLoading] = useState(true);

    // Tours and rentals for this district
    const [tours,         setTours]         = useState([]);
    const [toursLoading,  setToursLoading]  = useState(true);
    const [rentals,       setRentals]       = useState([]);
    const [rentalsLoading,setRentalsLoading]= useState(true);

    // Equipment edit/delete dialog state
    const [equipEditOpen,   setEquipEditOpen]   = useState(false);
    const [equipEditTarget, setEquipEditTarget] = useState(null);
    const [equipDelOpen,    setEquipDelOpen]    = useState(false);
    const [equipDelTarget,  setEquipDelTarget]  = useState(null);
    const [equipAddOpen,    setEquipAddOpen]    = useState(false);
    const [equipAddType,    setEquipAddType]    = useState('');

    // Filters
    const [filterType,    setFilterType]    = useState('ALL');
    const [filterCond,    setFilterCond]    = useState('ALL');
    const [filterAddedBy, setFilterAddedBy] = useState('ALL');
    const [filterAvail,   setFilterAvail]   = useState('ALL');
    const [filterCode,    setFilterCode]    = useState('');
    const [sortField,     setSortField]     = useState('none');
    const [showSort,      setShowSort]      = useState(false);
    const [showFilter,    setShowFilter]    = useState(false);

    const hasActiveFilters = filterType !== 'ALL' || filterCond !== 'ALL' ||
        filterAddedBy !== 'ALL' || filterAvail !== 'ALL' || filterCode !== '';

    const resetFilters = () => {
        setFilterType('ALL'); setFilterCond('ALL');
        setFilterAddedBy('ALL'); setFilterAvail('ALL'); setFilterCode('');
    };

    // Dialogs
    const [bikeEditOpen,  setBikeEditOpen]  = useState(false);
    const [bikeTarget,    setBikeTarget]    = useState(null);
    const [codeOpen,      setCodeOpen]      = useState(false);
    const [codeTarget,    setCodeTarget]    = useState(null);
    const [deleteOpen,    setDeleteOpen]    = useState(false);
    const [deleteTarget,  setDeleteTarget]  = useState(null);

    useEffect(() => {
        if (user && !isAdmin && !isManager) navigate('/');
    }, [user, isAdmin, isManager, navigate]);

    useEffect(() => {
        if (!id) return;
        axios.get(`/districts/view/id/${id}/staff`)
            .then(res => setDetails(res.data))
            .catch(err => { if (err.response?.status === 403) setNotAllowed(true); })
            .finally(() => setLoading(false));
    }, [id]);

    useEffect(() => {
        if (!id) return;
        axios.get(`/bicycles/view/district/${id}`)
            .then(res => setBicycles(Array.isArray(res.data) ? res.data : []))
            .catch(() => setBicycles([]))
            .finally(() => setBikeLoading(false));
    }, [id]);

    useEffect(() => {
        if (!id) return;
        axios.get(`/equipment/district/${id}`)
            .then(res => setEquipment(Array.isArray(res.data) ? res.data : []))
            .catch(() => setEquipment([]))
            .finally(() => setEquipLoading(false));
    }, [id]);

    useEffect(() => {
        if (!id) return;
        axios.get(`/api/tours/by-district/${id}`)
            .then(res => setTours(Array.isArray(res.data) ? res.data : []))
            .catch(() => setTours([]))
            .finally(() => setToursLoading(false));
    }, [id]);

    useEffect(() => {
        if (!id) return;
        axios.get(`/rentals/view/district/${id}`)
            .then(res => setRentals(Array.isArray(res.data) ? res.data : []))
            .catch(() => setRentals([]))
            .finally(() => setRentalsLoading(false));
    }, [id]);

    useEffect(() => {
        if (isAdmin) {
            axios.get('/districts/view?size=1000')
                .then(res => setDistricts(Array.isArray(res.data) ? res.data : (res.data?.content || [])))
                .catch(() => {});
        }
    }, [isAdmin]);

    // Split equipment by type
    const helmets  = useMemo(() => equipment.filter(e => e.equipmentType === 'HELMET'),   [equipment]);
    const locks    = useMemo(() => equipment.filter(e => e.equipmentType === 'LOCK'),      [equipment]);
    const kidSeats = useMemo(() => equipment.filter(e => e.equipmentType === 'KID_SEAT'), [equipment]);
    const bags     = useMemo(() => equipment.filter(e => e.equipmentType === 'BAG'),       [equipment]);

    // Unique added-by names for filter dropdown
    const uniqueAddedBy = useMemo(() =>
        [...new Set(bicycles.map(b => b.addedByUsername).filter(Boolean))].sort()
    , [bicycles]);

    // Filtered + sorted bicycles
    const filteredBikes = useMemo(() => {
        let list = [...bicycles];
        if (filterType    !== 'ALL') list = list.filter(b => b.type === filterType);
        if (filterCond    !== 'ALL') list = list.filter(b => b.condition === filterCond);
        if (filterAddedBy !== 'ALL') list = list.filter(b => b.addedByUsername === filterAddedBy);
        if (filterAvail === 'available') list = list.filter(b => b.available);
        if (filterAvail === 'inuse')     list = list.filter(b => !b.available);
        if (filterCode) list = list.filter(b => (b.code || '').toLowerCase().includes(filterCode.toLowerCase()));
        if (sortField !== 'none') {
            list.sort((a, b) => {
                if (sortField === 'available') return (a.available === b.available ? 0 : a.available ? -1 : 1);
                const av = (a[sortField] ?? '').toString().toLowerCase();
                const bv = (b[sortField] ?? '').toString().toLowerCase();
                return av.localeCompare(bv);
            });
        }
        return list;
    }, [bicycles, filterType, filterCond, filterAddedBy, filterAvail, filterCode, sortField]);

    const handleBikeSaved   = (updated) => setBicycles(prev => prev.map(b => b.id === updated.id ? updated : b));
    const handleBikeDeleted = (id)      => setBicycles(prev => prev.filter(b => b.id !== id));

    const handleEquipSaved   = (updated)  => setEquipment(prev => prev.map(e => e.id === updated.id ? updated : e));
    const handleEquipDeleted = (id)       => setEquipment(prev => prev.filter(e => e.id !== id));
    const handleEquipAdded   = (newItem)  => setEquipment(prev => [...prev, newItem]);

    if (!user || (!isAdmin && !isManager)) return null;

    if (loading) return (
        <Box sx={{ minHeight: '100vh', bgcolor: PARCHMENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress sx={{ color: TERRACOTTA }} />
        </Box>
    );

    if (notAllowed) return (
        <Box sx={{ minHeight: '100vh', bgcolor: PARCHMENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6" sx={{ color: WALNUT }}>{t('districts.not_allowed', 'You do not have access to this district.')}</Typography>
            <Button component="a" href="/districts" sx={{ color: TERRACOTTA }}>{t('districts.back', 'Back to Districts')}</Button>
        </Box>
    );

    if (!details) return null;
    const tc = typeColor(details.type);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: PARCHMENT }}>
            {/* Banner */}
            <Box sx={{
                background: `linear-gradient(160deg, ${WALNUT} 0%, #6B3A20 50%, ${TERRACOTTA} 100%)`,
                height: 130, position: 'relative', overflow: 'hidden',
                '&::after': { content: '""', position: 'absolute', bottom: 0, left: 0, right: 0, height: '40px', background: PARCHMENT, borderRadius: '40px 40px 0 0' },
            }} />

            <Container maxWidth="lg" sx={{ mt: -4, pb: 8, position: 'relative', zIndex: 1 }}>

                {/* Back + title row */}
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                    <IconButton component="a" href="/districts" size="small"
                        sx={{ color: TERRACOTTA, border: '1px solid rgba(196,98,45,0.2)', borderRadius: 2 }}>
                        <ArrowBackIcon fontSize="small" />
                    </IconButton>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="overline" sx={{ color: FOREST, letterSpacing: '0.2em', fontWeight: 700 }}>
                            {t('districts.overline', 'Districts')}
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Typography variant="h4" fontWeight={900}
                                sx={{ color: WALNUT, fontFamily: "'Playfair Display', Georgia, serif" }}>
                                {details.name}
                            </Typography>
                            <Chip label={details.type} size="small" sx={{ ...tc, fontWeight: 700, fontSize: '0.72rem' }} />
                        </Stack>
                    </Box>
                    {canEdit && (
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditOpen(true)}
                                sx={{ borderColor: TERRACOTTA, color: TERRACOTTA, borderRadius: 2, fontWeight: 700,
                                    '&:hover': { bgcolor: 'rgba(196,98,45,0.06)' } }}>
                                {t('common.edit')}
                            </Button>
                            {isAdmin && (
                                <Button variant="outlined" color="error" startIcon={<DeleteIcon />}
                                    onClick={() => setDelDistOpen(true)}
                                    sx={{ borderRadius: 2, fontWeight: 700 }}>
                                    {t('districts.delete_btn', 'Delete')}
                                </Button>
                            )}
                        </Stack>
                    )}
                </Stack>

                {/* Main 2-column grid */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '340px 1fr' }, gap: 4, alignItems: 'start' }}>

                    {/* District info */}
                    <Box sx={{ bgcolor: 'white', borderRadius: 3, overflow: 'hidden',
                        boxShadow: '0 2px 16px rgba(44,31,20,0.07)', border: '1px solid rgba(196,98,45,0.1)' }}>
                        <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(196,98,45,0.1)', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationCityIcon sx={{ color: TERRACOTTA, fontSize: '1.2rem' }} />
                            <Typography variant="subtitle1" fontWeight={700} sx={{ color: WALNUT }}>{t('districts.info', 'District Info')}</Typography>
                        </Box>
                        <Box sx={{ px: 3, py: 1 }}>
                            <InfoRow icon={<LocationCityIcon fontSize="small" />} label={t('districts.name', 'Name')} value={details.name} />
                            <InfoRow icon={<MapIcon fontSize="small" />} label={t('districts.region', 'Region')} value={details.region} />
                            <InfoRow icon={<LocationCityIcon fontSize="small" />} label={t('districts.type', 'Type')} value={details.type} />
                            <InfoRow icon={<LocationOnIcon fontSize="small" />} label={t('districts.address', 'Address')} value={details.address} />
                            <InfoRow icon={<EmailIcon fontSize="small" />} label={t('districts.email', 'Email')} value={details.email} />
                        </Box>
                    </Box>

                    {/* Staff */}
                    <Stack spacing={3}>
                        {/* Managers */}
                        <Box sx={{ bgcolor: 'white', borderRadius: 3, overflow: 'hidden',
                            boxShadow: '0 2px 16px rgba(44,31,20,0.07)', border: '1px solid rgba(196,98,45,0.1)' }}>
                            <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(196,98,45,0.1)' }}>
                                <SectionHeader icon={<ManageAccountsIcon />} title={t('districts.managers', 'Managers')} count={details.managers?.length ?? 0} />
                            </Box>
                            <Box sx={{ px: 3, py: 2 }}>
                                {details.managers?.length > 0 ? (
                                    <Stack spacing={1}>
                                        {details.managers.map(m => (
                                            <StaffCard key={m.id} name={m.username} email={m.email} imageUrl={m.imageUrl}
                                                sub={m.startDate ? `${t('districts.since', 'Since')} ${new Date(m.startDate).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}` : null} />
                                        ))}
                                    </Stack>
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>{t('districts.no_managers', 'No managers assigned.')}</Typography>
                                )}
                            </Box>
                        </Box>

                        {/* Guides */}
                        <Box sx={{ bgcolor: 'white', borderRadius: 3, overflow: 'hidden',
                            boxShadow: '0 2px 16px rgba(44,31,20,0.07)', border: '1px solid rgba(196,98,45,0.1)' }}>
                            <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(196,98,45,0.1)' }}>
                                <SectionHeader icon={<GroupsIcon />} title={t('districts.guides', 'Guides')} count={details.guides?.length ?? 0} />
                            </Box>
                            <Box sx={{ px: 3, py: 2 }}>
                                {details.guides?.length > 0 ? (
                                    <Stack spacing={1}>
                                        {details.guides.map(g => (
                                            <StaffCard key={g.id} name={g.user?.username} email={g.user?.email} imageUrl={g.user?.imageUrl}
                                                sub={g.stars != null ? `${'★'.repeat(Math.min(g.stars, 5))} ${g.stars} ${t('districts.stars', 'stars')}` : null} />
                                        ))}
                                    </Stack>
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>{t('districts.no_guides', 'No guides assigned.')}</Typography>
                                )}
                            </Box>
                        </Box>
                    </Stack>
                </Box>

                {/* ── Bicycles (full width) ──────────────────────────────────── */}
                <Box sx={{ mt: 4, bgcolor: 'white', borderRadius: 3, overflow: 'hidden',
                    boxShadow: '0 2px 16px rgba(44,31,20,0.07)', border: '1px solid rgba(196,98,45,0.1)' }}>

                    {/* Header */}
                    <Box sx={{ px: 3, py: 2, borderBottom: '1px solid rgba(196,98,45,0.1)',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                        <SectionHeader icon={<DirectionsBikeIcon />}
                            title={t('bicycle.section_title', 'Bicycles')} count={filteredBikes.length} />
                        <Button variant="contained" startIcon={<AddIcon />} size="small"
                            onClick={() => navigate('/bicycles/create', { state: { districtId: id, districtName: details.name } })}
                            sx={{ bgcolor: TERRACOTTA, '&:hover': { bgcolor: '#9A4A1F' }, borderRadius: 2, fontWeight: 700 }}>
                            {t('bicycle.add_btn', '+ Add Bicycle')}
                        </Button>
                    </Box>

                    {/* Sort & filter bar */}
                    <Box sx={{ px: 3, py: 1.5, borderBottom: '1px solid rgba(196,98,45,0.08)',
                        bgcolor: 'rgba(247,243,238,0.5)' }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            {/* Sort button + floating dropdown */}
                            <Box sx={{ position: 'relative' }}>
                                <Button size="small" variant="outlined" startIcon={<SortIcon />}
                                        onClick={() => setShowSort(p => !p)}
                                        sx={{ color: TERRACOTTA, borderColor: 'rgba(196,98,45,0.35)',
                                            fontWeight: 600, textTransform: 'none' }}>
                                    {t('bicycle.sort_label', 'Sort')}
                                    {sortField !== 'none' && (
                                        <Chip label={t(`bicycle.sort_${sortField}`, sortField)} size="small"
                                              sx={{ ml: 0.8, height: 18, fontSize: '0.7rem',
                                                  bgcolor: 'rgba(196,98,45,0.12)', color: TERRACOTTA }} />
                                    )}
                                </Button>
                                {showSort && (
                                    <Paper elevation={4} sx={{ position: 'absolute', top: '110%', left: 0, zIndex: 200,
                                        minWidth: 190, border: '1px solid rgba(196,98,45,0.15)', borderRadius: 2 }}>
                                        {[
                                            { value: 'none',      label: t('bicycle.sort_none', 'Default') },
                                            { value: 'type',      label: t('bicycle.sort_type', 'Type') },
                                            { value: 'condition', label: t('bicycle.sort_condition', 'Condition') },
                                            { value: 'code',      label: t('bicycle.sort_code', 'Code') },
                                            { value: 'available', label: t('bicycle.sort_availability', 'Availability') },
                                        ].map(opt => (
                                            <MenuItem key={opt.value} selected={sortField === opt.value}
                                                      onClick={() => { setSortField(opt.value); setShowSort(false); }}
                                                      sx={{ fontSize: '0.85rem',
                                                          '&.Mui-selected': { bgcolor: 'rgba(196,98,45,0.08)' } }}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Paper>
                                )}
                            </Box>

                            {/* Filter button */}
                            <Button size="small"
                                    variant={hasActiveFilters ? 'contained' : 'outlined'}
                                    startIcon={<FilterListIcon />}
                                    onClick={() => setShowFilter(p => !p)}
                                    sx={{ color: hasActiveFilters ? 'white' : TERRACOTTA,
                                        bgcolor: hasActiveFilters ? TERRACOTTA : 'transparent',
                                        borderColor: 'rgba(196,98,45,0.35)',
                                        fontWeight: 600, textTransform: 'none',
                                        '&:hover': { bgcolor: hasActiveFilters ? '#9A4A1F' : 'rgba(196,98,45,0.06)' } }}>
                                {t('bicycle.filter_btn', 'Filter')}
                                {hasActiveFilters && ` (active)`}
                            </Button>

                            {/* Clear filters */}
                            {hasActiveFilters && (
                                <Tooltip title={t('bicycle.clear_filters', 'Clear filters')}>
                                    <IconButton size="small" onClick={resetFilters}
                                                sx={{ color: 'text.secondary' }}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}

                            <Box sx={{ flexGrow: 1 }} />

                            <Typography variant="caption" color="text.secondary">
                                {filteredBikes.length} {filteredBikes.length === 1
                                    ? t('bicycle.result_one', 'bicycle')
                                    : t('bicycle.result_other', 'bicycles')}
                            </Typography>
                        </Stack>

                        {/* Collapsible filter panel */}
                        <Collapse in={showFilter}>
                            <Paper elevation={0} sx={{ p: 2.5, mt: 1.5,
                                border: '1px solid rgba(196,98,45,0.15)', borderRadius: 2,
                                bgcolor: 'rgba(247,243,238,0.7)' }}>
                                <Typography variant="caption" fontWeight={700}
                                            sx={{ textTransform: 'uppercase', letterSpacing: '0.08em',
                                                color: 'text.secondary', display: 'block', mb: 2 }}>
                                    {t('bicycle.filter_title', 'Filter Bicycles')}
                                </Typography>
                                <Grid container spacing={2}>
                                    {/* Code search */}
                                    <Grid item xs={12} sm={6} md={3}>
                                        <TextField fullWidth size="small"
                                                   label={t('bicycle.search_code', 'Search code')}
                                                   value={filterCode}
                                                   onChange={e => setFilterCode(e.target.value)}
                                                   InputProps={{ startAdornment:
                                                       <InputAdornment position="start">
                                                           <SearchIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                                                       </InputAdornment>
                                                   }} />
                                    </Grid>
                                    {/* Type */}
                                    <Grid item xs={12} sm={6} md={2}>
                                        <TextField select fullWidth size="small"
                                                   label={t('bicycle.filter_type', 'Type')}
                                                   value={filterType}
                                                   onChange={e => setFilterType(e.target.value)}>
                                            <MenuItem value="ALL">{t('bicycle.filter_all', 'All')}</MenuItem>
                                            {BIKE_TYPES.map(bt => <MenuItem key={bt} value={bt}>{bt}</MenuItem>)}
                                        </TextField>
                                    </Grid>
                                    {/* Condition */}
                                    <Grid item xs={12} sm={6} md={2}>
                                        <TextField select fullWidth size="small"
                                                   label={t('bicycle.filter_condition', 'Condition')}
                                                   value={filterCond}
                                                   onChange={e => setFilterCond(e.target.value)}>
                                            <MenuItem value="ALL">{t('bicycle.filter_all', 'All')}</MenuItem>
                                            {CONDITIONS.map(c => <MenuItem key={c} value={c}>{c.replace('_', ' ')}</MenuItem>)}
                                        </TextField>
                                    </Grid>
                                    {/* Availability */}
                                    <Grid item xs={12} sm={6} md={2}>
                                        <TextField select fullWidth size="small"
                                                   label={t('bicycle.filter_availability', 'Availability')}
                                                   value={filterAvail}
                                                   onChange={e => setFilterAvail(e.target.value)}>
                                            <MenuItem value="ALL">{t('bicycle.filter_all', 'All')}</MenuItem>
                                            <MenuItem value="available">{t('bicycle.filter_available', 'Available')}</MenuItem>
                                            <MenuItem value="inuse">{t('bicycle.filter_in_use', 'In use')}</MenuItem>
                                        </TextField>
                                    </Grid>
                                    {/* Added by */}
                                    {uniqueAddedBy.length > 0 && (
                                        <Grid item xs={12} sm={6} md={3}>
                                            <TextField select fullWidth size="small"
                                                       label={t('bicycle.filter_added_by', 'Added by')}
                                                       value={filterAddedBy}
                                                       onChange={e => setFilterAddedBy(e.target.value)}>
                                                <MenuItem value="ALL">{t('bicycle.filter_all', 'All')}</MenuItem>
                                                {uniqueAddedBy.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                                            </TextField>
                                        </Grid>
                                    )}
                                </Grid>
                            </Paper>
                        </Collapse>
                    </Box>

                    {/* List */}
                    <Box sx={{ px: 3, py: 2 }}>
                        {bikeLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress size={28} sx={{ color: TERRACOTTA }} />
                            </Box>
                        ) : filteredBikes.length > 0 ? (
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' }, gap: 1.5 }}>
                                {filteredBikes.map(b => (
                                    <BicycleRow key={b.id} bicycle={b}
                                        onEdit={bk => { setBikeTarget(bk); setBikeEditOpen(true); }}
                                        onAssignCode={bk => { setCodeTarget(bk); setCodeOpen(true); }}
                                        onDelete={bk => { setDeleteTarget(bk); setDeleteOpen(true); }}
                                    />
                                ))}
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                {bicycles.length === 0
                                    ? t('bicycle.no_bicycles', 'No bicycles assigned to this district.')
                                    : t('bicycle.no_match', 'No bicycles match the current filters.')}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* ── Helmets ───────────────────────────────────────────── */}
                <EquipmentSection
                    icon={<SportsMotorsportsIcon sx={{ fontSize: '1.1rem' }} />}
                    title={t('equipment.helmets', 'Helmets')}
                    items={helmets} loading={equipLoading}
                    emptyKey="equipment.no_helmets" emptyDefault="No helmets assigned to this district."
                    canEdit={canEdit}
                    onEdit={item  => { setEquipEditTarget(item); setEquipEditOpen(true); }}
                    onDelete={item => { setEquipDelTarget(item);  setEquipDelOpen(true);  }}
                    onAdd={() => { setEquipAddType('HELMET'); setEquipAddOpen(true); }}
                />

                {/* ── Locks ─────────────────────────────────────────────── */}
                <EquipmentSection
                    icon={<LockIcon sx={{ fontSize: '1.1rem' }} />}
                    title={t('equipment.locks', 'Locks')}
                    items={locks} loading={equipLoading}
                    emptyKey="equipment.no_locks" emptyDefault="No locks assigned to this district."
                    canEdit={canEdit}
                    onEdit={item  => { setEquipEditTarget(item); setEquipEditOpen(true); }}
                    onDelete={item => { setEquipDelTarget(item);  setEquipDelOpen(true);  }}
                    onAdd={() => { setEquipAddType('LOCK'); setEquipAddOpen(true); }}
                />

                {/* ── Kid Seats ─────────────────────────────────────────── */}
                <EquipmentSection
                    icon={<EventSeatIcon sx={{ fontSize: '1.1rem' }} />}
                    title={t('equipment.kid_seats', 'Kid Seats')}
                    items={kidSeats} loading={equipLoading}
                    emptyKey="equipment.no_kid_seats" emptyDefault="No kid seats assigned to this district."
                    canEdit={canEdit}
                    onEdit={item  => { setEquipEditTarget(item); setEquipEditOpen(true); }}
                    onDelete={item => { setEquipDelTarget(item);  setEquipDelOpen(true);  }}
                    onAdd={() => { setEquipAddType('KID_SEAT'); setEquipAddOpen(true); }}
                />

                {/* ── Bags ──────────────────────────────────────────────── */}
                <EquipmentSection
                    icon={<ShoppingBagIcon sx={{ fontSize: '1.1rem' }} />}
                    title={t('equipment.bags', 'Bags')}
                    items={bags} loading={equipLoading}
                    emptyKey="equipment.no_bags" emptyDefault="No bags assigned to this district."
                    canEdit={canEdit}
                    onEdit={item  => { setEquipEditTarget(item); setEquipEditOpen(true); }}
                    onDelete={item => { setEquipDelTarget(item);  setEquipDelOpen(true);  }}
                    onAdd={() => { setEquipAddType('BAG'); setEquipAddOpen(true); }}
                />

                {/* ── Tours ────────────────────────────────────────────── */}
                <Box sx={{ mt: 4, bgcolor: 'white', borderRadius: 3, overflow: 'hidden',
                    boxShadow: '0 2px 16px rgba(44,31,20,0.07)', border: '1px solid rgba(196,98,45,0.1)' }}>
                    <Box sx={{ px: 3, py: 2, borderBottom: '1px solid rgba(196,98,45,0.1)' }}>
                        <SectionHeader icon={<EventSeatIcon sx={{ fontSize: '1.1rem' }} />}
                            title={t('districts.tours', 'Tours')} count={tours.length} />
                    </Box>
                    <Box sx={{ px: 3, py: 2 }}>
                        {toursLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress size={28} sx={{ color: TERRACOTTA }} />
                            </Box>
                        ) : tours.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                {t('districts.no_tours', 'No tours for this district yet.')}
                            </Typography>
                        ) : tours.map(tour => (
                            <Paper key={tour.id} elevation={0} sx={{
                                display: 'flex', alignItems: 'center', gap: 2, p: 1.5, mb: 1,
                                border: '1px solid rgba(196,98,45,0.1)', borderRadius: 2, bgcolor: 'white',
                                '&:hover': { boxShadow: '0 2px 10px rgba(44,31,20,0.07)' },
                            }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={700} sx={{ color: WALNUT }}>
                                        {tour.offerTitle || '—'}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {tour.time ? new Date(tour.time).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                                        {tour.guideName ? ` · ${tour.guideName}` : ''}
                                    </Typography>
                                </Box>
                                <Chip label={tour.status || '—'} size="small" sx={{
                                    fontWeight: 700, fontSize: '0.7rem',
                                    bgcolor: tour.status === 'COMPLETED' ? '#e8f5e9'
                                           : tour.status === 'CANCELLED' ? '#ffebee'
                                           : 'rgba(196,98,45,0.1)',
                                    color: tour.status === 'COMPLETED' ? '#388e3c'
                                         : tour.status === 'CANCELLED' ? '#d32f2f'
                                         : TERRACOTTA,
                                }} />
                            </Paper>
                        ))}
                    </Box>
                </Box>

                {/* ── Rentals ──────────────────────────────────────────── */}
                <Box sx={{ mt: 4, bgcolor: 'white', borderRadius: 3, overflow: 'hidden',
                    boxShadow: '0 2px 16px rgba(44,31,20,0.07)', border: '1px solid rgba(196,98,45,0.1)' }}>
                    <Box sx={{ px: 3, py: 2, borderBottom: '1px solid rgba(196,98,45,0.1)' }}>
                        <SectionHeader icon={<DirectionsBikeIcon sx={{ fontSize: '1.1rem' }} />}
                            title={t('districts.rentals', 'Rentals')} count={rentals.length} />
                    </Box>
                    <Box sx={{ px: 3, py: 2 }}>
                        {rentalsLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress size={28} sx={{ color: TERRACOTTA }} />
                            </Box>
                        ) : rentals.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                {t('districts.no_rentals', 'No rentals for this district yet.')}
                            </Typography>
                        ) : rentals.map(rental => (
                            <Paper key={rental.id} elevation={0} sx={{
                                display: 'flex', alignItems: 'center', gap: 2, p: 1.5, mb: 1,
                                border: '1px solid rgba(196,98,45,0.1)', borderRadius: 2, bgcolor: 'white',
                                '&:hover': { boxShadow: '0 2px 10px rgba(44,31,20,0.07)' },
                            }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={700} sx={{ color: WALNUT }}>
                                        {rental.bikeType?.replace('_', ' ')} × {rental.quantity}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {rental.startTime ? new Date(rental.startTime).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                                        {rental.durationType ? ` · ${rental.durationType}` : ''}
                                    </Typography>
                                </Box>
                                <Chip label={rental.status || '—'} size="small" sx={{
                                    fontWeight: 700, fontSize: '0.7rem',
                                    bgcolor: rental.status === 'COMPLETED' ? '#e8f5e9'
                                           : rental.status === 'CANCELLED' ? '#ffebee'
                                           : 'rgba(196,98,45,0.1)',
                                    color: rental.status === 'COMPLETED' ? '#388e3c'
                                         : rental.status === 'CANCELLED' ? '#d32f2f'
                                         : TERRACOTTA,
                                }} />
                            </Paper>
                        ))}
                    </Box>
                </Box>

                {/* ── District Hours (full width) ───────────────────────── */}
                <DistrictHoursSection districtId={id} canEdit={canEdit} t={t} />

            </Container>

            {/* Edit district */}
            <EditDistrictDialog open={editOpen} district={details} onClose={() => setEditOpen(false)}
                onSaved={updated => setDetails(prev => ({ ...prev, ...updated }))} />

            {/* Edit bicycle */}
            <EditBicycleDialog open={bikeEditOpen} bicycle={bikeTarget} districts={districts}
                isAdmin={isAdmin} currentUser={user}
                onClose={() => setBikeEditOpen(false)} onSaved={handleBikeSaved} />

            {/* Assign code */}
            <AssignCodeDialog open={codeOpen} bicycle={codeTarget} districtName={details?.name}
                onClose={() => setCodeOpen(false)} onSaved={handleBikeSaved} />

            {/* Delete bicycle */}
            <DeleteBicycleDialog open={deleteOpen} bicycle={deleteTarget} currentUser={user}
                onClose={() => setDeleteOpen(false)} onDeleted={handleBikeDeleted} />

            {/* Edit equipment */}
            <EditEquipmentDialog open={equipEditOpen} item={equipEditTarget}
                onClose={() => setEquipEditOpen(false)} onSaved={handleEquipSaved} />

            {/* Delete equipment */}
            <DeleteEquipmentDialog open={equipDelOpen} item={equipDelTarget} currentUser={user}
                onClose={() => setEquipDelOpen(false)} onDeleted={handleEquipDeleted} />

            {/* Add equipment */}
            <AddEquipmentDialog open={equipAddOpen} equipmentType={equipAddType} districtId={id}
                onClose={() => setEquipAddOpen(false)} onAdded={handleEquipAdded} />

            {/* Delete district */}
            <DeleteDistrictDialog open={delDistOpen} districtName={details?.name} districtId={id}
                currentUser={user} onClose={() => setDelDistOpen(false)}
                onDeleted={() => navigate('/districts')} />
        </Box>
    );
}
